import React, { useState, useEffect } from "react";
import "../styles/live.css";
import "moment-timezone";
import { Link } from "react-router-dom";
import { Trophy } from "lucide-react";
import { fetchAllTournaments } from "../../../../common/services/tournament";
import {
  Tournament,
  TournamentStatus,
} from "../../../../common/services/tournament.types";
import { useAuth } from "../../../../common/store/authStore";
import TournamentCard from "../components/tournament-card";

function LiveTournaments() {
  const { authenticatedUser, getUserCountry } = useAuth();
  const isLoggedIn = !!authenticatedUser;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [filter, setFilter] = useState<TournamentStatus>(
    TournamentStatus.UPCOMING,
  );
  const [loadingState, setLoadingState] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTournamentData() {
      try {
        setLoadingState(true);
        const userCountry = getUserCountry();
        const data = await fetchAllTournaments(currentPage, 10, filter, userCountry);
        setCurrentPage(data.page);
        setTotalPages(data.pages);
        setTournaments(data.tournaments);
      } catch {
        setError("Failed to fetch tournaments");
      } finally {
        setLoadingState(false);
      }
    }
    fetchTournamentData();
  }, [filter, currentPage, getUserCountry]);

  const handleFilterChange = (newFilter: TournamentStatus) => {
    setFilter(newFilter);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div id="join-tournaments-section" className="live-tournaments-section">
      <div className="tournaments-background">
        <div className="tournaments-overlay"></div>
        <div className="tournaments-particles"></div>
      </div>
      <div className="tournaments-container">
        <div className="tournaments-header">
          <div className="section-badge">
            <Trophy className="badge-icon" size={20} />
            <span>LIVE TOURNAMENTS</span>
          </div>
          <h2 className="section-title">
            Join the <span className="title-highlight">Battle</span>
          </h2>
          <p className="section-subtitle">
            Connect with amazing and competitive gamers all across Africa. Earn
            while doing what you love the most.
          </p>
        </div>
        <div className="tournament-filters">
          <button
            className={`filter-btn${filter === TournamentStatus.UPCOMING ? " active" : ""}`}
            onClick={() => handleFilterChange(TournamentStatus.UPCOMING)}
          >
            {TournamentStatus.UPCOMING}
          </button>
          <button
            className={`filter-btn${filter === TournamentStatus.LIVE ? " active" : ""}`}
            onClick={() => handleFilterChange(TournamentStatus.LIVE)}
          >
            {TournamentStatus.LIVE}
          </button>
          <button
            className={`filter-btn${filter === TournamentStatus.COMPLETED ? " active" : ""}`}
            onClick={() => handleFilterChange(TournamentStatus.COMPLETED)}
          >
            {TournamentStatus.COMPLETED}
          </button>
        </div>
        {loadingState ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <span>Fetching Tournaments... ...</span>
          </div>
        ) : (
          <div className="live-tournaments-grid">
            {tournaments.length === 0 ? (
              <div className="no-tournaments">
                <div className="no-tournaments-content">
                  <Trophy className="no-tournaments-icon" size={64} />
                  <h3>
                    No {filter.charAt(0).toUpperCase() + filter.slice(1)}{" "}
                    Tournaments
                  </h3>
                  <p>There are currently no {filter} tournaments available.</p>
                  {filter !== "completed" && (
                    <Link
                      className="create-tournament-btn"
                      to={isLoggedIn ? "/create-tournament" : "/login"}
                    >
                      <span>Create Tournament</span>
                      <Trophy className="btn-icon" size={20} />
                    </Link>
                  )}
                </div>
              </div>
            ) : (
              tournaments.map((tournament) => (
                <TournamentCard
                  key={tournament._id}
                  tournament={tournament}
                  userCountry={getUserCountry()}
                />
              ))
            )}
          </div>
        )}
        {error && <div className="error-message">{error}</div>}

        {totalPages > 1 && (
          <div
            className="pagination-controls"
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 16,
              marginTop: 32,
            }}
          >
            <button
              className="pagination-btn"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span style={{ color: "#fff", alignSelf: "center" }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default LiveTournaments;
